import { Component, ViewChild } from '@angular/core';
import { CardComponent } from '@shared/components/card/card.component';
import {
  AuthRoles,
  EstimationGroupMeteringPointSearchResult,
  EstimationGroupMeteringPointsService,
  MeteringPointType,
  PagedResultOfEstimationGroupMeteringPointSearchResult,
  VeeService
} from '@app/api/dmc';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { TableComponent } from '@shared/components/table/table.component';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { estimationGroupMeteringPointTable } from '@shared/table-configs/vee/vee-meteringpoint.table';
import { HeaderService } from '@app/core/services/header.service';
import { Plus, Trash } from 'lucide-angular';
import { ModalService } from '@app/core/services/modal.service';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import {
  VeeMeteringpointAttachComponent
} from '@shared/modals/vee/vee-meteringpoint-attach/vee-meteringpoint-attach.component';
import {
  VeeMeteringpointRemoveComponent
} from '@shared/modals/vee/vee-meteringpoint-remove/vee-meteringpoint-remove.component';
import { VeeMeteringPointFormGroup } from '@shared/modals/vee/vee-meteringpoint.formgroup';
import { removeNullValues } from '@app/core/utils/remove-null-values';
import { Observable } from 'rxjs';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { SortParam } from '@app/core/constants/sort-param';
import { hasRole } from '@app/core/guards/has-role.guard';

@Component({
  selector: 'app-vee-meteringpoint',
  imports: [
    CardComponent,
    TableComponent,
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    TranslatePipe,
    InputDirective,
    ButtonDirective,
    SelectDirective
  ],
  templateUrl: './vee-meteringpoint.component.html',
  styleUrl: './vee-meteringpoint.component.css'
})
export class VeeMeteringpointComponent {
  @ViewChild('table', { static: false }) public table?: TableComponent<any>;

  /** Form **/
  public searchForm: FormGroup = new FormGroup(VeeMeteringPointFormGroup);

  /** ID **/
  public id!: string;

  /** Options **/
  public meteringPointTypeOptions: KeyValue[] = enumToKeyValue(MeteringPointType, { omitKeys: ['NotSpecified'], prefixValue: 'enum.meteringpoint.meteringpointtype.' });

  /** Table **/
  public tableConfig: TableConfig<EstimationGroupMeteringPointSearchResult> = {
    ...estimationGroupMeteringPointTable,
    serviceCallback: (pageSize: number, page: number, order?: SortParam) => this.search(pageSize, page, order),
  }

  /** SelectedElements **/
  public selectedElements: any[] = [];

  constructor(
    public readonly route: ActivatedRoute,
    private readonly estimationGroupMeteringPointsService: EstimationGroupMeteringPointsService,
    private readonly headerMenuService: HeaderService,
    private readonly veeService: VeeService,
    private readonly modalService: ModalService,
  ) {
    this.route.parent?.paramMap.subscribe((q: ParamMap) => {
      this.id = q.get('id')!;

      this.table?.search()

      const meteringpointOrVeeWriter = hasRole([AuthRoles.MeteringPointWriter, AuthRoles.VEEWriter]);

      this.headerMenuService.setActions([
          {
            id: 'attachMeteringPoint',
            title: 'vee.meteringpoint.attachMeteringPoint',
            icon: Plus,
          },
          {
            id: 'removeMeteringPoint',
            title: 'vee.meteringpoint.removeMeteringPoint',
            icon: Trash,
            iconColor: 'text-red-500'
          }
        ],
        {
          attachMeteringPoint: () => this.openAttachModal(),
          removeMeteringPoint: () => this.openRemoveModal(),
        },
        {
          attachMeteringPoint: () => !meteringpointOrVeeWriter,
          removeMeteringPoint: () => !meteringpointOrVeeWriter || this.selectedElements.length === 0,
        })
    });
  }

  public search(pageSize: number, page: number, order?: SortParam): Observable<PagedResultOfEstimationGroupMeteringPointSearchResult> {
    const data = removeNullValues(this.searchForm.value);

    const sort = order ? { SortParams: [order] } : {};

    return this.estimationGroupMeteringPointsService.veeEstimationgroupsEstimationGroupIdMeteringpointsSearchPost(this.id ?? '', { ...data, page, pageSize, ...sort });
  }

  public openAttachModal(): void {
    this.modalService.show({
      component: VeeMeteringpointAttachComponent,
      title: 'vee.meteringpoint.attachMeteringPoint',
      size: 'xl',
      actions: [
        { type: 'primary', label: 'system.button.attach', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { estimationGroupId: this.id },
    }).subscribe(() => {
      this.table?.search()
      })
  }
  public openRemoveModal(): void {
    this.modalService.show({
        component: VeeMeteringpointRemoveComponent,
        title: 'vee.meteringpoint.removeMeteringPoint',
        type: 'center',
        size: 'sm',
        actions: [
          { type: 'primary', label: 'common.yes', method: 'submit' },
          { type: 'secondary', label: 'common.no', closeOnClick: true },
        ],
        params: { estimationGroupId: this.id, elements: this.selectedElements },
      }
    ).subscribe(() => {
      this.table?.search()
    })
  }
}
